import styled, {css} from "styled-components";
import PopupCloseButton from "@FRAMEWORK/ui/desktop/button/PopupCloseButton";
import {useRef, useState, useEffect, MutableRefObject} from "react";
import {PopupProps} from "@FRAMEWORK/ui/types/d";

interface Props extends PopupProps {
	size?: "small" | "regular" | "large" | "full";
	paddingTop?: boolean;
	show?: boolean;
	animate?: "basic" | "slideIn";
	height?: number;
	close?: () => void;
	ref?: MutableRefObject<HTMLElement>;
}

const Wrap = styled.div<{
	isScroll?: boolean;
	animate?: "basic" | "slideIn";
	show?: boolean;
	size?: string;
	isPartnerPage?: string;
}>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 101;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	width: 100%;
	height: 100%;
	padding: 50px 0;
	background-color: rgba(0, 0, 0, 0.6);
	overflow-y: ${props => (props.isScroll ? "scroll" : "hidden")};
	box-sizing: border-box;
	opacity: 0;
	visibility: ${props => (props.show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;

	animation: ${props =>
		props.show
			? css`
				fadeIn 0.3s ease forwards
			`
			: css`
				fadeOut 0.3s ease forwards
			`};

	@media print {
		position: relative;
		width: 210mm;
		height: auto;
		margin: 0;
		padding: 0;
		overflow: visible;
		break-after: auto;
	}

	@keyframes fadeIn {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@keyframes fadeOut {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}

	//출발일 보기 팝업
	${props =>
		props.size === "full" &&
		`
		padding: 0;
	`}
`;

const FullContent = styled.div<{size?: string; isPartnerPage?: string}>`
	${props =>
		props.size === "full" &&
		`
	width: 100%;
	height: 100%;
	background-color: #fff;
	> div {
		margin: 0 auto;
	}
	`}

	${props => {
		if (props.isPartnerPage === "gmarket" && props.size !== "full") {
			return `
			width: calc(100% - 40px);
		`;
		}
	}}
`;

const Content = styled.div<{
	size?: "small" | "regular" | "large" | "full";
	animate?: "basic" | "slideIn";
	show?: boolean;
	height?: number;
	isPartnerPage?: string;
}>`
	position: relative;
	z-index: 105;
	display: flex;
	flex-direction: column;
	flex: 0 0 auto;
	padding-top: 0;
	background-color: #fff;
	visibility: ${props => (props.show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;

	${props => {
		if (props.isPartnerPage === "gmarket") {
			return css`
				width: 100%;
				min-width: 100%;
				padding-top: 70px;
			`;
		}
		if (props.size === "small") {
			return css`
				width: 500px;
				min-width: 500px;
			`;
		}
		if (props.size === "regular") {
			return css`
				width: 800px;
				min-width: 800px;
			`;
		}
		if (props.size === "large") {
			return css`
				width: 990px;
				min-width: 990px;
			`;
		}
		if (props.size === "full") {
			return css`
				height: 100%;
			`;
		}
	}}

	@media print {
		display: block;
		width: 210mm;
		min-width: 210mm;
		height: auto;
		padding: 0;
		margin: 0;
	}
`;

const Header = styled.div<{
	size?: "small" | "regular" | "large" | "full";
	isPartnerPage?: string;
}>`
	display: flex;
	flex: 0 0 70px;
	width: ${props => {
		if (props.size === "small") {
			return "500px";
		}
		if (props.size === "regular") {
			return "800px";
		}
		if (props.size === "large") {
			return "990px";
		}
	}};

	> div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex: 1 1 auto;
		margin: 0 40px;
		border-bottom: 1px solid var(--emp-color);
	}

	${props => {
		if (props.isPartnerPage === "gmarket") {
			return `
				position: absolute;
				top: 0;
				left: 50%;
				width: 100%;
				height: 70px;
				transform: translateX(-50%);
			`;
		}
	}}

	${props =>
		props.size === "full" &&
		`
		position: sticky;
		top: -244px;
		width: 1200px;
		// padding-right: 15px;
		margin: 0 auto;
		> div {
			margin: 0;
		}
	`}

	@media print {
		display: none;
	}
`;

const Container = styled.div<{
	paddingTop: boolean;
	animate?: "basic" | "slideIn";
	size?: string;
	isPartnerPage?: string;
}>`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	padding: ${props => (props.paddingTop ? "40px;" : "0 40px 40px;")};
	overflow-y: auto;

	@media print {
		display: block;
		width: 210mm;
		height: auto;
		padding: 0;
		margin: 0;
	}

	${props =>
		props.size === "full" &&
		`
		padding: 0;
		margin-top: 0;
	`}
`;

const Title = styled.h3`
	display: -webkit-box;
	padding-right: 30px;
	font-size: 22px;
	color: var(--emp-color);
	font-family: "PT-medium";
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: normal;
	word-wrap: break-word;
	word-break: break-all;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	line-clamp: 1;
`;

const useResizeObserver = (ref: MutableRefObject<HTMLDivElement>) => {
	const [dimension, setDimentsion] = useState(0);

	useEffect(() => {
		const observerTarget = ref.current ? ref.current : document.querySelector(".popup_content");
		const resizeObserver = new ResizeObserver(entries => {
			entries.forEach(entry => {
				setDimentsion(entry.target.clientHeight);
			});
		});
		resizeObserver.observe(observerTarget);
		return () => {
			resizeObserver.unobserve(observerTarget);
		};
	}, []);

	return dimension;
};

const PopupContainer = ({
	title,
	children,
	size = "small",
	paddingTop = true,
	close,
	animate = "basic",
	show = true,
	height,
}: Props) => {
	const wrapRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const [wrapHeight, setWrapHeight] = useState(0);
	const [contentHeight, setContentHeight] = useState(0);
	const [isScroll, setIsScroll] = useState(false);

	const dimension = useResizeObserver(contentRef);
	const [partnerPage, setPartnerPage] = useState("");
	const partners = window.location.pathname.indexOf("partners") > 0;
	const gmarket = window.location.pathname.indexOf("gmarket") > 0;

	useEffect(() => {
		if (partners) {
			if (gmarket) {
				setPartnerPage("gmarket");
			}
		}
	}, [partnerPage]);

	useEffect(() => {
		setIsScroll(wrapHeight < dimension);
	}, [dimension]);

	useEffect(() => {
		if (wrapRef.current && contentRef.current) {
			setWrapHeight(wrapRef.current?.offsetHeight);
			setContentHeight(dimension);
		}
	}, [wrapRef.current, contentHeight, dimension]);

	useEffect(() => {
		if (show) {
			document.body.style.overflowY = "hidden";
		}
		return () => {
			const showCount = document.querySelectorAll("[data-show='true']")?.length || 0;
			if (showCount > 0) return;
			document.body.style.removeProperty("overflow-y");
		};
	}, [show]);
	return (
		<Wrap
			ref={wrapRef}
			data-show={String(show)}
			show={show}
			animate={animate}
			isScroll={isScroll}
			size={size}
			isPartnerPage={partnerPage}
		>
			<FullContent size={size} isPartnerPage={partnerPage}>
				<Content
					className="popup_content"
					ref={contentRef}
					show={show}
					animate={animate}
					size={size}
					height={height || wrapHeight}
					isPartnerPage={partnerPage}
				>
					<Header size={size} isPartnerPage={partnerPage}>
						<div>
							<Title>{title || "안내"}</Title>
							<PopupCloseButton onClose={close} />
						</div>
					</Header>
					<Container
						animate={animate}
						paddingTop={paddingTop ? true : paddingTop}
						size={size}
						isPartnerPage={partnerPage}
					>
						{children}
					</Container>
				</Content>
			</FullContent>
		</Wrap>
	);
};

export default PopupContainer;
